import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'src/store';
import { setStoredActiveSettingsId } from 'src/store/slices/configurationSlice';
import type {
  ConfigurationSettings,
  ConfigurationTemplateFields,
} from 'src/services/configurationService';
import { usePagedConfigurationSettings } from 'src/pages/ConfigurationPage/hooks/usePagedConfigurationSettings';

// Owns the list of a project's configuration settings versions: paginated
// fetching/merging, and resolving which version is "active" - the user's
// last stored choice, else the deployed version, else the most recent one -
// persisting that choice back to the store.
export const useConfigurationSettingsList = (
  id: string,
  defaultTemplateFields: ConfigurationTemplateFields | undefined,
) => {
  const dispatch = useAppDispatch();
  const configuration = useAppSelector((s) => s.configurationSlice);

  const [activeSettings, setActiveSettings] = useState<ConfigurationSettings | null>(null);
  const [isCreationMode, setIsCreationMode] = useState(true);

  const { settings, isLoadingAllConfigurationSettings, resetToFirstPage } =
    usePagedConfigurationSettings(id, { skip: !(id && defaultTemplateFields) }, []);

  useEffect(() => {
    if (!defaultTemplateFields) {
      return;
    }

    const deployedSettings = settings.find((item) => item.is_deployed);
    const storedSettings =
      configuration[id] &&
      settings.find((item) => item.id === configuration[id]?.storedActiveSettingsId);

    if (!activeSettings && storedSettings) {
      setActiveSettings(storedSettings);
      return;
    }

    if (!activeSettings && deployedSettings) {
      setActiveSettings(deployedSettings);
      return;
    }

    if (!activeSettings && settings.length) {
      setActiveSettings(settings[0]);
    }

    if (activeSettings) {
      setActiveSettings(settings.find((item) => item.id === activeSettings.id));
    }
  }, [settings, defaultTemplateFields]);

  useEffect(() => {
    if (!activeSettings) {
      return;
    }

    setIsCreationMode(
      typeof activeSettings.is_deployed === 'boolean' ? activeSettings.is_deployed : true,
    );
    dispatch(setStoredActiveSettingsId({ id, storedActiveSettingsId: activeSettings.id }));
  }, [activeSettings]);

  // Used when cancelling out of "new version" creation mode (activeSettings
  // was cleared): puts the active version back using the same priority order
  // as the resolution effect above.
  const restoreActiveSettingsFallback = () => {
    if (activeSettings) {
      return;
    }

    const deployedSettings = settings.find((item) => item.is_deployed);
    const storedSettings =
      configuration[id] &&
      settings.find((item) => item.id === configuration[id]?.storedActiveSettingsId);
    const fallback = storedSettings || deployedSettings || settings[0];

    if (fallback) {
      setActiveSettings(fallback);
    }
  };

  return {
    settings,
    activeSettings,
    setActiveSettings,
    isCreationMode,
    setIsCreationMode,
    isLoadingAllConfigurationSettings,
    resetToFirstPage,
    restoreActiveSettingsFallback,
  };
};
